import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Spinner,
  Stack,
  Text,
  VStack,
  Icon,
  SimpleGrid,
  Divider
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  IoSchool,
  IoBusinessOutline,
  IoTimeOutline,
  IoDesktopOutline,
  IoArrowForward,
  IoCalendarOutline
} from "react-icons/io5";
import ApiResponse from "../../services/ApiResponse";
import { Carrera } from "../../services/models/Carrera";
import { useNavigate } from "react-router-dom";
import UnauthorizedPage from "../UnauthorizedPage";
import ServerErrorPage from "../ServerErrorPage";
import { Helmet } from "react-helmet-async";


const HomePage = () => {
  const [response, setResponse] = useState(new ApiResponse<Carrera[]>());
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCarreras = async () => {
      const apiResponse = new ApiResponse<Carrera[]>();
      await apiResponse.useFetch("carreras", "GET");
      setResponse(apiResponse);
    };
    fetchCarreras();
  }, []);

  const contentLo = () => (
    <Flex justify="center" align="center" py={20}>
      <Spinner size="xl" color="white" thickness="4px" />
    </Flex>
  );

  const contentOk = () => (
    <VStack align="stretch" spacing={8}>
      {/* ================= EVENTOS ================= */}
      <VStack spacing={4} textAlign="center">
        <Heading size="xl">Eventos educativos</Heading>

        <Text fontSize="lg" opacity={0.85} maxW="700px">
          Capacitaciones, charlas, jornadas y actividades impulsadas o acompañadas
          por la Secretaría de Educación de Goya.
        </Text>

        <Button
          size="lg"
          bg="blue.400"
          color="white"
          rightIcon={<IoArrowForward />}
          px={10}
          borderRadius="full"
          _hover={{ bg: "blue.500", transform: "scale(1.05)" }}
          onClick={() => navigate("/eventos")}
        >
          Ver eventos
        </Button>
      </VStack>

      <Divider borderColor="whiteAlpha.300" />

      {/* ================= CARRERAS ================= */}
      <Flex justify="space-between" align="center">
        <Heading size="lg">Carreras destacadas</Heading>
        <Button
          variant="link"
          color="white"
          rightIcon={<IoArrowForward />}
          onClick={() => navigate("/carreras")}
        >
          Ver todas
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {response.data?.slice(0, 6).map((degree) => (
          <Box
            key={degree.id}
            p={6}
            bg="whiteAlpha.100"
            borderRadius="2xl"
            border="1px solid"
            borderColor="whiteAlpha.200"
            cursor="pointer"
            transition="all 0.3s"
            _hover={{ bg: "whiteAlpha.200", transform: "translateY(-5px)" }}
            onClick={() => navigate(`/carreras/${degree.id}`)}
          >
            <VStack align="start" spacing={3}>
              <Heading size="md" lineHeight="shorter">
                {degree.nombre}
              </Heading>

              <Stack direction="row" wrap="wrap" spacing={4} fontSize="sm">
                <Flex align="center">
                  <Icon as={IoBusinessOutline} mr={2} color="yellow.400" />
                  <Text fontWeight="bold" color="yellow.400">
                    {degree.institucion_nombre?.toUpperCase()}
                  </Text>
                </Flex>

                <Flex align="center" color="whiteAlpha.800">
                  <Icon as={IoTimeOutline} mr={2} />
                  <Text>{degree.duracion_anios} años</Text>
                </Flex>

                <Flex align="center" color="whiteAlpha.800">
                  <Icon as={IoDesktopOutline} mr={2} />
                  <Text>{degree.modalidad}</Text>
                </Flex>
              </Stack>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>
    </VStack>
  );

  return (
    <>
    <Helmet>
      <title>
        Goya Educa | Secretaría de Educación de la Municipalidad de Goya
      </title>
      <meta
        name="description"
        content="Plataforma oficial de la Secretaría de Educación de la Municipalidad de Goya. Información sobre carreras, instituciones, eventos educativos y oportunidades de formación."
      />
      <meta
        name="keywords"
        content="Goya, educación, carreras, eventos educativos, municipalidad de Goya, formación"
      />
    </Helmet>
    <Box
      bgGradient="linear(to-br, blue.700, blue.900)"
      minH="100vh"
      color="white"
    >
      <Box
        py={{ base: 12, md: 20 }}
        px={4}
        textAlign="center"
        bg="whiteAlpha.100"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
      >
        <Container maxW="container.lg">
          <Heading size="3xl" mb={4} letterSpacing="tight">
            Bienvenido a{" "}
            <Text as="span" color="blue.300">
              Goya Educa
            </Text>
          </Heading>

          <Text fontSize="md" opacity={0.75} mb={4}>
            Secretaría de Educación · Municipalidad de Goya
          </Text>

          <Text fontSize="xl" mb={10} opacity={0.85} maxW="800px" mx="auto">
            La plataforma oficial de la Secretaría de Educación de la Municipalidad
            de Goya, destinada a acercar a la comunidad información sobre carreras,
            instituciones, eventos educativos y oportunidades de formación.
          </Text>

          <Stack
            direction={{ base: "column", sm: "row" }}
            spacing={4}
            justify="center"
          >
            <Button
              onClick={() => navigate("/carreras")}
              size="lg"
              bg="white"
              color="blue.700"
              leftIcon={<IoSchool />}
              px={10}
              borderRadius="full"
              _hover={{ transform: "scale(1.05)", bg: "blue.50" }}
            >
              Ver Carreras
            </Button>

            <Button
              onClick={() => navigate("/eventos")}
              size="lg"
              variant="outline"
              colorScheme="whiteAlpha"
              color="white"
              leftIcon={<IoCalendarOutline />}
              px={10}
              borderRadius="full"
              _hover={{ bg: "whiteAlpha.200", transform: "scale(1.05)" }}
            >
              Eventos
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={14}>
        {response.loading ? (
          contentLo()
        ) : response.responseCode === 200 ? (
          contentOk()
        ) : response.responseCode === 401 ? (
          <UnauthorizedPage />
        ) : (
          <ServerErrorPage />
        )}
      </Container>
    </Box>
    </>
  );
};

export default HomePage;
